import { useEffect, useRef, useState } from 'react';
import { alpha, theme } from '../theme.js';
import {
  useRoster, useSetRosterWeek, useClearRosterWeek,
  useDutyRoster, useSetDutyWeek, useClearDutyWeek,
} from '../lib/queries.js';
import { shortDate, addDays } from '../lib/shifts.js';
import { leaveDuring } from './LeaveCard.js';
import type { RosterWeek, Schedule, Team, OrgMember, DutyWeek, Holiday } from '@keetcheck/shared';

interface SchedAsset { assetId: string; assetName: string; slot: number; sortOrder: number; active: boolean }
interface Slot { slot: number; label: string; assetIds: string[] }

/** Teams named after a colour (Blue, Red…) get that colour as a swatch. */
const SWATCH: Record<string, [string, string]> = {
  blue: ['#3fa9f5', '#fff'], green: ['#22a447', '#fff'], red: ['#e5322d', '#fff'],
  yellow: ['#ffe14d', '#1d1e22'], orange: ['#f7931e', '#fff'], purple: ['#8e44ad', '#fff'],
  pink: ['#ff6fb5', '#fff'], black: ['#222', '#fff'], white: ['#fff', '#1d1e22'], grey: ['#999', '#fff'],
};

export function TeamChip({ name }: { name: string | null | undefined }) {
  if (!name) return <span style={{ color: theme.textMuted }}>—</span>;
  const sw = SWATCH[name.trim().toLowerCase()];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700,
      background: sw ? sw[0] : theme.surface2, color: sw ? sw[1] : theme.text,
      border: `1px solid ${sw ? 'transparent' : theme.border}`, whiteSpace: 'nowrap',
    }}>{name}</span>
  );
}

/**
 * The unit roster, week by week, laid out like the published sheet: which
 * team checks what (from the check schedule, if there is one), the Duty and
 * Ops Support crews, and the UDO / day UDO. Tap a week to change it.
 */
export function RosterGrid({ orgId, schedule, teams, members }: {
  orgId: string; schedule: Schedule | null; teams: Team[]; members: OrgMember[];
}) {
  const [weeks, setWeeks] = useState(12);
  const [editing, setEditing] = useState<string | null>(null);
  const { data: roster, isLoading } = useDutyRoster(orgId, weeks);
  // The grid scrolls sideways on phones; the week editor stays pinned to the
  // visible width so its controls never need sideways scrolling.
  const scroller = useRef<HTMLDivElement>(null);
  const [visibleWidth, setVisibleWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    setVisibleWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setVisibleWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { data: plan = [] } = useRoster(orgId, schedule?.id ?? '', weeks + 1);

  const sAssets = ((schedule?.assets ?? []) as unknown as SchedAsset[]).filter((a) => a.active);
  const slots: Slot[] = [...new Set(sAssets.map((a) => a.slot))].sort((a, b) => a - b).map((slot) => {
    const in_ = sAssets.filter((a) => a.slot === slot).sort((a, b) => a.sortOrder - b.sortOrder);
    return { slot, label: in_.map((a) => a.assetName).join(' + '), assetIds: in_.map((a) => a.assetId) };
  });
  const teamName = (id: string | null | undefined) => teams.find((t) => t.id === id)?.name ?? null;
  const personName = (id: string | null | undefined) => members.find((m) => m.userId === id)?.displayName ?? null;
  const slotTeam = (w: RosterWeek | undefined, s: Slot) => {
    if (!w) return null;
    const ids = new Set(s.assetIds.map((id) => w.assignments.find((a) => a.assetId === id)?.teamId ?? null));
    return ids.size === 1 ? [...ids][0]! : 'mixed';
  };
  const planFor = (ps: string) => plan.find((p) => p.periodStart === ps);
  // A week's holidays: Tue → next Mon (a Monday holiday ends the week before's long weekend).
  const holidaysIn = (ps: string) => (roster?.holidays ?? []).filter((h) => h.date > ps && h.date <= addDays(ps, 7));
  const weekRows = roster?.weeks ?? [];
  const thisWeek = weekRows[0]?.periodStart;
  const cols = slots.length + 5;

  const th: React.CSSProperties = {
    padding: '6px 8px', fontSize: 11, fontWeight: 700, color: theme.textDim, textAlign: 'left',
    borderBottom: `1px solid ${theme.border}`, background: theme.surface2, verticalAlign: 'bottom',
  };
  const td: React.CSSProperties = { padding: '6px 8px', fontSize: 12, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' };

  return (
    <div>
      {isLoading && <div style={{ fontSize: 12, color: theme.textMuted }}>Loading…</div>}
      <div ref={scroller} style={{ overflowX: 'auto', border: `1px solid ${theme.border}`, borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: theme.font }}>
          <thead>
            <tr>
              <th style={th}>Week</th>
              <th style={th}>Duty Crew</th>
              <th style={th}>Ops Support</th>
              <th style={th}>UDO</th>
              <th style={th}>Day UDO</th>
              {slots.map((s) => <th key={s.slot} style={{ ...th, minWidth: 90, color: theme.textMuted }}>{s.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {weekRows.map((w) => {
              const p = planFor(w.periodStart);
              const hols = holidaysIn(w.periodStart);
              const dayUdo = w.dayUdoUserId ?? roster?.defaultDayUdoId ?? null;
              const scratch = w.rostered && !w.dutyTeamId && !w.opsTeamId;
              return [
                <tr
                  key={w.periodStart}
                  onClick={() => setEditing(editing === w.periodStart ? null : w.periodStart)}
                  style={{
                    cursor: 'pointer',
                    background: w.periodStart === thisWeek ? alpha(theme.accent, 7) : theme.surface,
                  }}
                >
                  <td style={{ ...td, fontWeight: 700 }}>
                    {shortDate(w.periodStart)}
                    {!w.rostered && <div style={{ fontSize: 10, fontWeight: 700, color: theme.status.warn.fg }}>not rostered</div>}
                    {hols.map((h) => (
                      <div key={h.date} style={{ fontSize: 10, fontWeight: 500, color: theme.status.service.fg }}>
                        {shortDate(h.date)} {h.name}
                      </div>
                    ))}
                  </td>
                  {scratch
                    ? <td colSpan={2} style={{ ...td, fontStyle: 'italic', color: theme.textDim, background: theme.status.warn.bg }}>
                        {w.note || 'No crews rostered'}
                      </td>
                    : <>
                        <td style={td}><TeamChip name={teamName(w.dutyTeamId)} /></td>
                        <td style={td}><TeamChip name={teamName(w.opsTeamId)} /></td>
                      </>}
                  <td style={td}>{personName(w.udoUserId) ?? <span style={{ color: theme.textMuted }}>—</span>}</td>
                  <td style={{ ...td, color: w.dayUdoUserId ? theme.text : theme.textMuted }}>
                    {personName(dayUdo) ?? '—'}
                  </td>
                  {slots.map((s) => {
                    const t = slotTeam(p, s);
                    return <td key={s.slot} style={td}>{t === 'mixed' ? 'mixed' : <TeamChip name={teamName(t)} />}</td>;
                  })}
                </tr>,
                w.note && !scratch && (
                  <tr key={`${w.periodStart}-note`} onClick={() => setEditing(w.periodStart)} style={{ cursor: 'pointer' }}>
                    <td colSpan={cols} style={{ ...td, paddingTop: 0, fontSize: 11, color: theme.status.danger.fg, fontWeight: 700 }}>
                      {w.note}
                    </td>
                  </tr>
                ),
                editing === w.periodStart && (
                  <tr key={`${w.periodStart}-edit`}>
                    <td colSpan={cols} style={{ padding: 0, borderBottom: `1px solid ${theme.border}` }}>
                      <div style={{ position: 'sticky', left: 0, width: visibleWidth ?? '100%', boxSizing: 'border-box' }}>
                      <WeekEditor
                        orgId={orgId} scheduleId={schedule?.id ?? null} week={w} plan={p} slots={slots} teams={teams}
                        members={members} defaultDayUdoId={roster?.defaultDayUdoId ?? null} holidays={hols}
                        slotTeam={slotTeam} onDone={() => setEditing(null)}
                      />
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
      <button
        onClick={() => setWeeks(weeks + 12)}
        style={{ marginTop: 8, background: 'none', border: 'none', color: theme.accent, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: theme.font }}
      >
        Show 12 more weeks
      </button>
    </div>
  );
}

function WeekEditor({ orgId, scheduleId, week, plan, slots, teams, members, defaultDayUdoId, holidays, slotTeam, onDone }: {
  orgId: string; scheduleId: string | null; week: DutyWeek; plan: RosterWeek | undefined; slots: Slot[];
  teams: Team[]; members: OrgMember[]; defaultDayUdoId: string | null; holidays: Holiday[];
  slotTeam: (w: RosterWeek | undefined, s: Slot) => string | null; onDone: () => void;
}) {
  const setChecks = useSetRosterWeek(orgId, scheduleId ?? '');
  const clearChecks = useClearRosterWeek(orgId, scheduleId ?? '');
  const setDuty = useSetDutyWeek(orgId);
  const clearDuty = useClearDutyWeek(orgId);
  const active = teams.filter((t) => t.active);
  const udos = members.filter((m) => m.udo);
  const weekEnd = addDays(week.periodStart, 7);
  const udoLabel = (m: OrgMember) => m.displayName + (leaveDuring(m.leave, week.periodStart, weekEnd) ? (m.leave!.endsOn ? ' (on leave)' : ' (paused)') : '');
  const initialSlots = Object.fromEntries(slots.map((s) => {
    const t = slotTeam(plan, s);
    return [s.slot, t && t !== 'mixed' ? t : ''];
  })) as Record<number, string>;
  const [bySlot, setBySlot] = useState<Record<number, string>>(initialSlots);
  const [duty, setDutyTeam] = useState(week.dutyTeamId ?? '');
  const [ops, setOps] = useState(week.opsTeamId ?? '');
  const [udo, setUdo] = useState(week.udoUserId ?? '');
  const [dayUdo, setDayUdo] = useState(week.dayUdoUserId ?? '');
  const [note, setNote] = useState(week.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const busy = setChecks.isPending || setDuty.isPending || clearDuty.isPending || clearChecks.isPending;
  const defaultName = members.find((m) => m.userId === defaultDayUdoId)?.displayName;

  async function save() {
    setError(null);
    if (duty && duty === ops) { setError('Duty Crew and Operational Support Crew must be different teams.'); return; }
    try {
      await setDuty.mutateAsync({
        periodStart: week.periodStart,
        dutyTeamId: duty || null, opsTeamId: ops || null,
        udoUserId: udo || null, dayUdoUserId: dayUdo || null, note: note.trim() || null,
      });
      const checksChanged = slots.some((s) => (bySlot[s.slot] ?? '') !== (initialSlots[s.slot] ?? ''));
      if (scheduleId && checksChanged) {
        await setChecks.mutateAsync({
          periodStart: week.periodStart,
          note: note.trim() || null,
          assignments: slots.flatMap((s) => s.assetIds.map((assetId) => ({ assetId, teamId: bySlot[s.slot] || null }))),
        });
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    }
  }

  function scratch() {
    setBySlot(Object.fromEntries(slots.map((s) => [s.slot, ''])));
    setDutyTeam(''); setOps('');
    if (!note) setNote('Scratch crews and UDOs');
  }

  // `taken`: the team already picked for the other crew, which this one can't also be.
  const pickTeam = (value: string, onChange: (v: string) => void, label: string, taken?: string) => (
    <label key={label} style={row}>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle} aria-label={label}>
        <option value="">— none</option>
        {active.map((t) => <option key={t.id} value={t.id} disabled={!!taken && t.id === taken}>{t.name}</option>)}
      </select>
    </label>
  );

  return (
    <div style={{ padding: 12, background: theme.surface2, display: 'flex', flexDirection: 'column', gap: 8, whiteSpace: 'normal' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>
        Week of {shortDate(week.periodStart)} <span style={{ fontWeight: 500, color: theme.textMuted }}>· Mon 6pm → Mon 6am</span>
      </div>
      {holidays.length > 0 && (
        <div style={{ fontSize: 11, color: theme.status.service.fg }}>
          Public holiday{holidays.length > 1 ? 's' : ''}: {holidays.map((h) => `${shortDate(h.date)} ${h.name}`).join(', ')} — crew, UDO and ops support also cover the day.
        </div>
      )}
      {pickTeam(duty, setDutyTeam, 'Duty Crew', ops)}
      {pickTeam(ops, setOps, 'Operational Support Crew', duty)}
      <label style={row}>
        <span style={{ flex: 1, minWidth: 0 }}>UDO (nights + weekend)</span>
        <select value={udo} onChange={(e) => setUdo(e.target.value)} style={selectStyle} aria-label="UDO">
          <option value="">— not picked</option>
          {udos.map((m) => <option key={m.userId} value={m.userId}>{udoLabel(m)}</option>)}
        </select>
      </label>
      <label style={row}>
        <span style={{ flex: 1, minWidth: 0 }}>Day UDO (weekdays 6am–6pm)</span>
        <select value={dayUdo} onChange={(e) => setDayUdo(e.target.value)} style={selectStyle} aria-label="Day UDO">
          <option value="">{defaultName ? `Unit default (${defaultName})` : '— unit default (not set)'}</option>
          {udos.map((m) => <option key={m.userId} value={m.userId}>{udoLabel(m)}</option>)}
        </select>
      </label>
      {udos.length === 0 && (
        <div style={{ fontSize: 11, color: theme.status.warn.fg }}>
          No one is marked “On UDO roster” yet — set that on the Members tab first.
        </div>
      )}
      {scheduleId && slots.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', marginTop: 4 }}>Checks</div>
          {slots.map((s) => pickTeam(bySlot[s.slot] ?? '', (v) => setBySlot({ ...bySlot, [s.slot]: v }), s.label))}
        </>
      )}
      <input
        value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional), e.g. POWER / BOAT"
        aria-label="Note" style={{ ...selectStyle, width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
      />
      {error && <div style={{ fontSize: 12, color: theme.status.danger.fg }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => void save()} disabled={busy} style={{ ...btn, background: theme.accent, color: theme.accentInk, border: 'none' }}>
          {busy ? 'Saving…' : 'Save week'}
        </button>
        <button onClick={scratch} style={btn}>Scratch week (no teams)</button>
        {week.rostered && (
          <button onClick={() => clearDuty.mutate(week.periodStart, { onSuccess: onDone })} disabled={busy} style={btn}>
            Un-roster week
          </button>
        )}
        {scheduleId && plan?.source === 'roster' && (
          <button onClick={() => clearChecks.mutate(week.periodStart, { onSuccess: onDone })} disabled={busy} style={btn}>
            Checks: use rotation
          </button>
        )}
        <button onClick={onDone} style={{ ...btn, border: 'none', background: 'none' }}>Cancel</button>
      </div>
    </div>
  );
}

const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: theme.textDim };

const selectStyle: React.CSSProperties = {
  padding: '6px 8px', fontSize: 13, borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.surface, color: theme.text, fontFamily: theme.font, maxWidth: '55%',
};

const btn: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
  border: `1px solid ${theme.border}`, background: theme.surface, color: theme.textDim, fontFamily: theme.font,
};
